
import { Component, Prop, Vue } from 'vue-property-decorator';
import { resolveLocale } from '@/utils/i18n-helpers';
import Cookie from 'js-cookie';
import Modal from '@/components/shared/Modal.vue';

@Component<AnnouncementModal>({
	components: {
		Modal
	}
})
export default class AnnouncementModal extends Vue {
	@Prop({ type: Boolean, required: true }) private showModal!: boolean;
	@Prop({ type: Array, required: true }) private unseenAnnouncements!: Announcement[];
	@Prop({ type: Array, required: true }) private announcements!: Announcement[];
	@Prop({ type: Object, required: true }) private restaurant!: Restaurant;

	private displayedAnnouncement: number = 0;
	private seenAnnouncements: SeenAnnouncements[] = [];

	/**
	 * Localize all unseen announcements
	 * If the message can't be localized, it will be filtered out
	 *
	 * @return {LocalizedAnnouncement[]}
	 */
	private get localizedAnnouncements(): LocalizedAnnouncement[] {
		// Determine the locale to use for localization
		// Use the current locale if it's supported, otherwise use the default locale
		// If there is no default locale, use 'en'
		const localeToUse: string = this.restaurant?.locales?.length
			? resolveLocale(this.$i18n.locale, this.restaurant.locales)
			: 'en';

		const announcements = this.unseenAnnouncements.map((announcement: Announcement) => {
			const localizedMessage = this.localizeMessage(announcement.message, localeToUse);
			if (!localizedMessage) {
				return null;
			}
			return {
				...announcement,
				message: localizedMessage
			};
		}).filter((announcement: LocalizedAnnouncement | null) => announcement) as LocalizedAnnouncement[];

		return announcements;
	}

	private mounted(): void {
		this.seenAnnouncements = JSON.parse(Cookie.get('seenAnnouncements') || '[]');

		// If there are announcements, mark the first one as seen
		// Otherwise, close the modal
		if (this.localizedAnnouncements.length) {
			this.seenAnnouncements.push({
				id: this.localizedAnnouncements[0].id,
				updated_at: this.localizedAnnouncements[0].updated_at,
				restaurantId: this.restaurant.id
			});
		}
		else {
			this.closeModal();
		}
	}

	/**
	 * Get the localized announcement message
	 * If the localeToUse is not available, fallback to 'en'
	 * If 'en' is not available, fallback to the first key in the object (should probably never get to, but just in case)
	 * If all other fallbacks fail, return an empty string (will be filtered out)
	 *
	 * @param {{ [key: string]: string }} message
	 * @param {string} localeToUse
	 * @return {string}
	 */
	private localizeMessage(message: { [key: string]: string }, localeToUse: string): string {
		return (
			message[localeToUse] ??
			message['en'] ??
			message[Object.keys(message)[0]] ??
			''
		);
	}

	/**
	 * Display the next announcement and mark it as seen
	 *
	 * @return {void}
	 */
	private nextAnnouncement(): void {
		this.displayedAnnouncement < this.localizedAnnouncements.length - 1 ? this.displayedAnnouncement++ : this.displayedAnnouncement = 0;
		this.addAnnouncementToSeen(this.unseenAnnouncements[this.displayedAnnouncement]);
	}

	/**
	 * Display the previous announcement and mark it as seen
	 *
	 * @return {void}
	 */
	private prevAnnouncement(): void {
		this.displayedAnnouncement > 0 ? this.displayedAnnouncement-- : this.displayedAnnouncement = this.localizedAnnouncements.length - 1;
		this.addAnnouncementToSeen(this.unseenAnnouncements[this.displayedAnnouncement]);
	}

	/**
	 * Add an announcement to the seen list
	 *
	 * @param {Announcement} announcement
	 * @return {void}
	 */
	private addAnnouncementToSeen(announcement: Announcement): void {
		// If the announcement has already been seen at the current location, don't add it again
		if (this.seenAnnouncements.some((seenAnnouncement: SeenAnnouncements) => seenAnnouncement.id === announcement.id && seenAnnouncement.restaurantId === this.restaurant.id)) {
			return;
		}
		this.seenAnnouncements.push({
			id: announcement.id,
			updated_at: announcement.updated_at,
			restaurantId: this.restaurant.id
		});
	}

	/**
	 * Set announcements as seen and close the modal
	 *
	 * @return {void}
	 */
	private closeModal(): void {
		Cookie.set('seenAnnouncements', JSON.stringify(this.seenAnnouncements), { expires: 1 });
		this.$emit('close');
	}
}
